import api from '../index'

export interface FrontendUserItem {
  id: number
  uuid: string
  username: string
  nickname: string
  display_name?: string | null
  realname: string | null
  phone: string
  email: string | null
  avatar: string | null
  gender: number
  birthday: string | null
  signature: string | null
  level: number
  points: number
  status: number
  last_login_ip: string | null
  last_login_time: string | null
  login_count: number
  last_login_type: string | null
  is_logoff: number
  logoff_at: string | null
  pid: number
  total_direct: number
  total_valid_direct: number
  is_recommend: number
  register_ip: string | null
  register_source: string | null
  province?: string | null
  city?: string | null
  district?: string | null
  address?: string | null
  intro?: string | null
  introduce?: string | null
  constellation?: string | null
  occupation?: string | null
  height?: number
  show_cover?: string | null
  photo_album?: string[]
  interest_tags?: string[]
  is_high?: number
  is_new?: number
  operations_summary?: {
    following_count: number
    follower_count: number
    post_count: number
    goods_count: number
    buy_order_count: number
    sell_order_count: number
    complaint_count: number
  }
  protocol_agreement_summary?: {
    total_count: number
    latest_protocol_key: string
    latest_version: string
    latest_scene: string
    latest_agreed_at: string
  }
  message_risk_summary?: {
    blocked_count: number
    latest_risk_level: string
    latest_reject_reason: string
    latest_occurred_at: string
  }
  account_deletion_summary?: {
    request_no: string
    status: string
    applied_at: string
    processed_at: string
  }
  legal_hold_summary?: {
    active: number
    hold_id: number
    hold_no: string
    authority_name: string
    effective_at: string
    expires_at: string
  }
  profile?: {
    province?: string | null
    city?: string | null
  } | null
  created_at: string | null
  updated_at: string | null
}

export interface FrontendUserPagePayload<T = FrontendUserItem> {
  list: T[]
  total: number
  page: number
  per_page: number
  last_page?: number
}

export interface FrontendUserFormPayload {
  username: string
  password?: string
  nickname: string
  display_name?: string | null
  realname?: string | null
  phone?: string
  email?: string | null
  avatar?: string | null
  gender?: number
  birthday?: string | null
  signature?: string | null
  level?: number
  points?: number
  status?: number
  is_logoff?: number
  is_recommend?: number
  province?: string | null
  city?: string | null
  district?: string | null
  address?: string | null
  intro?: string | null
  introduce?: string | null
  constellation?: string | null
  occupation?: string | null
  height?: number
  show_cover?: string | null
  photo_album?: string[]
  interest_tags?: string[]
  is_high?: number
  is_new?: number
}

export interface FrontendUserPortraitPayload {
  social: {
    following_count: number
    follower_count: number
    profile_view_total: number
  }
  content: {
    post_count: number
    goods_count: number
    sold_goods_count: number
    recent_post_at: string | null
    recent_goods_at: string | null
  }
  trade: {
    buy_order_count: number
    sell_order_count: number
    paid_buy_order_count: number
    completed_sell_order_count: number
    buy_paid_amount: number
    sell_paid_amount: number
    refund_count: number
    recent_buy_order_at: string | null
    recent_sell_order_at: string | null
  }
  risk: {
    complaint_count: number
    post_report_count: number
    goods_report_count: number
    risk_event_count: number
  }
  activity: {
    login_count: number
    last_login_time: string | null
    created_at: string | null
  }
  recent_lists: {
    posts: Array<{
      id: number
      title: string
      status: number
      audit_status: number
      report_count: number
      like_count: number
      comment_count: number
      browse_count: number
      created_at: string | null
    }>
    goods: Array<{
      id: number
      title: string
      price: number
      display_status: string
      content_status: string
      report_count: number
      browse_count: number
      want_count: number
      like_count: number
      related_order_no: string
      related_order_status: string
      related_payment_status: string
      related_refund_status: string
      created_at: string | null
    }>
    buy_orders: Array<{
      id: number
      order_no: string
      goods_title_snapshot: string
      pay_price: number
      order_status: string
      payment_status: string
      refund_status: string
      created_at: string | null
    }>
    sell_orders: Array<{
      id: number
      order_no: string
      goods_title_snapshot: string
      pay_price: number
      order_status: string
      payment_status: string
      refund_status: string
      created_at: string | null
    }>
  }
}

export interface FrontendUserProtocolAgreementItem {
  id: number
  protocol_id: number
  protocol_key: string
  client_type: string
  title_snapshot: string
  version: string
  content_hash: string
  link_url: string
  scene: string
  device_id: string
  platform: string
  app_version: string
  ip: string
  user_agent: string
  agreed_at: string
  created_at: string | null
  updated_at: string | null
}

export interface FrontendUserMessageRiskLogItem {
  id: number
  callback_command: string
  conversation_id: string
  send_id: string
  recv_id: string
  group_id: string
  client_msg_id: string
  server_msg_id: string
  session_type: number
  content_type: number
  audit_status: string
  risk_level: string
  reject_reason: string
  hit_keywords_json?: string[]
  content_preview: string
  payload_json?: Record<string, unknown> | null
  occurred_at: string
  created_at: string | null
  updated_at: string | null
}

export interface FrontendUserAccountDeletionRequestItem {
  id: number
  user_id: number
  request_no: string
  status: string
  reason: string | null
  protocol_key: string
  protocol_version: string
  check_snapshot_json?: Record<string, unknown> | null
  blocked_snapshot_json?: Record<string, unknown> | null
  process_remark: string | null
  applied_at: string
  cancelled_at: string
  processed_at: string
  created_at: string | null
  updated_at: string | null
}

export type FrontendUserAvatarIdentityStatus = 'processing' | 'passed' | 'failed' | 'error' | 'invalidated'

export interface FrontendUserAvatarIdentityVerificationItem {
  id: number
  attachment_id: number
  attachment_url: string
  attachment_hash: string
  status: FrontendUserAvatarIdentityStatus
  provider: string
  product_code: string
  provider_request_id: string
  verify_score: number
  failure_code: string
  failure_message: string
  verified_at: string | null
  invalidated_at: string | null
  invalidation_reason: string
  created_at: string | null
}

export interface FrontendUserResetPasswordPayload {
  user_id: number
  password: string
}

export interface FrontendUserStatsPayload {
  filtered_total: number
  enabled_count: number
  recommended_count: number
  recent_register_count: number
  high_intent_count: number
}

export interface SelectOptionItem {
  value: number | string
  label: string
  disabled?: boolean
}

export function frontendUserPage(params: Record<string, unknown>) {
  return api.get<unknown, { code: number, message: string, data: FrontendUserPagePayload }>('/admin/user/user', {
    params,
  })
}

export function frontendUserDetail(id: number) {
  return api.get<unknown, { code: number, message: string, data: FrontendUserItem }>(`/admin/user/user/${id}`)
}

export function frontendUserStats(params?: Record<string, unknown>) {
  return api.get<unknown, { code: number, message: string, data: FrontendUserStatsPayload }>('/admin/user/user/stats', {
    params,
  })
}

export function frontendUserPortrait(id: number) {
  return api.get<unknown, { code: number, message: string, data: FrontendUserPortraitPayload }>(`/admin/user/user/${id}/portrait`)
}

export function frontendUserResetPassword(id: number) {
  return api.post<unknown, { code: number, message: string, data: FrontendUserResetPasswordPayload }>(`/admin/user/user/${id}/reset-password`)
}

export function frontendUserUpdateStatus(id: number, status: number) {
  return api.post(`/admin/user/user/${id}/status`, { status })
}

export function frontendUserCreate(data: FrontendUserFormPayload) {
  return api.post('/admin/user/user', data)
}

export function frontendUserUpdate(id: number, data: FrontendUserFormPayload) {
  return api.put(`/admin/user/user/${id}`, data)
}

export function frontendUserDelete(id: number) {
  return api.delete(`/admin/user/user/${id}`)
}

export function frontendUserBatchDelete(ids: number[]) {
  return api.delete('/admin/user/user/batch', {
    data: { ids },
  })
}

export function frontendUserOptions(params?: Record<string, unknown>) {
  return api.get<unknown, { code: number, message: string, data: SelectOptionItem[] }>('/admin/user/user/options', {
    params,
  })
}

export function frontendUserProtocolAgreements(id: number, params?: Record<string, unknown>) {
  return api.get<unknown, { code: number, message: string, data: FrontendUserPagePayload<FrontendUserProtocolAgreementItem> }>(`/admin/user/user/${id}/protocol-agreements`, {
    params,
  })
}

export function frontendUserMessageRiskLogs(id: number, params?: Record<string, unknown>) {
  return api.get<unknown, { code: number, message: string, data: FrontendUserPagePayload<FrontendUserMessageRiskLogItem> }>(`/admin/user/user/${id}/message-risk-logs`, {
    params,
  })
}

export function frontendUserAccountDeletionRequests(id: number, params?: Record<string, unknown>) {
  return api.get<unknown, { code: number, message: string, data: FrontendUserPagePayload<FrontendUserAccountDeletionRequestItem> }>(`/admin/user/user/${id}/account-deletion-requests`, {
    params,
  })
}

export function frontendUserAvatarIdentityVerifications(id: number) {
  return api.get<unknown, { code: number, message: string, data: FrontendUserAvatarIdentityVerificationItem[] }>(`/admin/user/user/${id}/avatar-identity-verifications`)
}

export function frontendUserInvalidateAvatarIdentity(id: number, verificationId: number, reason: string) {
  return api.post<unknown, { code: number, message: string, data: { id: number, status: FrontendUserAvatarIdentityStatus } }>(
    `/admin/user/user/${id}/avatar-identity-verifications/${verificationId}/invalidate`,
    { reason },
  )
}

const apiFrontendUser = {
  page: frontendUserPage,
  detail: frontendUserDetail,
  stats: frontendUserStats,
  portrait: frontendUserPortrait,
  resetPassword: frontendUserResetPassword,
  updateStatus: frontendUserUpdateStatus,
  create: frontendUserCreate,
  update: frontendUserUpdate,
  delete: frontendUserDelete,
  batchDelete: frontendUserBatchDelete,
  options: frontendUserOptions,
  protocolAgreements: frontendUserProtocolAgreements,
  messageRiskLogs: frontendUserMessageRiskLogs,
  accountDeletionRequests: frontendUserAccountDeletionRequests,
  avatarIdentityVerifications: frontendUserAvatarIdentityVerifications,
  invalidateAvatarIdentity: frontendUserInvalidateAvatarIdentity,
}

export default apiFrontendUser
